import { useMemo } from 'react';
import { FORECAST_EMPTY_STATES_TYPES } from './useEmptyStateType';
import {
  FiltersDudeIllustration,
  QuestionMarksDudeIllustration,
  LookingGlassDudeIllustration,
  OopsIllustration
} from '@linearb/linui-common';
import PurpleDudeWithFlagImg from './images/purple-dude-with-flag.svg';
import MissingFiltersActions from '../../../state/filters/components/empty-states/MissingFiltersActions';
import { ForecastFiltersContext } from '../../../state/filters/contexts';

const emptyStateViews = {
  [FORECAST_EMPTY_STATES_TYPES.MISSING_FILTERS]: {
    Illustration: FiltersDudeIllustration,
    title: 'Missing Filters',
    Description: (
      <>
        Please create a new project or load an existing project to view its
        forecast.
      </>
    ),
    Actions: <MissingFiltersActions Context={ForecastFiltersContext} />
  },
  [FORECAST_EMPTY_STATES_TYPES.EMPTY_DATA]: {
    Illustration: QuestionMarksDudeIllustration,
    title: 'Could not find any data',
    Description: (
      <>
        We were unable to retrieve data for the selected set of filters. <br />
        Please try and adjust your filter selection.
      </>
    )
  },
  [FORECAST_EMPTY_STATES_TYPES.INSUFFICIENT_LENGTH]: {
    Illustration: () => <LookingGlassDudeIllustration width='7rem' />,
    title: 'Insufficient Historical Data',
    Description: (
      <>
        Insufficient historical data for forecast analysis. <br />
        Please try again at a later date or adjust the filters.
      </>
    )
  },
  [FORECAST_EMPTY_STATES_TYPES.INSUFFICIENT_SCOPE]: {
    Illustration: () => <LookingGlassDudeIllustration width='7rem' />,
    title: 'Insufficient Data',
    Description: (
      <>
        Insufficient data for forecast analysis. <br />
        If possible, refine the filters to access more data needed to complete
        the forecast.
      </>
    )
  },
  [FORECAST_EMPTY_STATES_TYPES.ENTROPY_HIGH]: {
    Illustration: QuestionMarksDudeIllustration,
    title: 'Unpredictable Scope Detected',
    Description: (
      <>
        Data progression is not stable enough at this time
        <br /> for accurate analysis and forecasting.
        <br />
        <br />
        Please try again at a later date or adjust the filters.
      </>
    )
  },
  [FORECAST_EMPTY_STATES_TYPES.PROJECT_COMPLETED]: {
    Illustration: () => (
      <img src={PurpleDudeWithFlagImg} style={{ marginLeft: '7rem' }} alt='' />
    ),
    title: 'Great Job!',
    Description: (
      <>
        Our forecasting algorithm shows that you are at the finish line, keep
        pushing forward! <br />
        If this is not the case, please adjust your filters to reflect the
        relevant scope
      </>
    )
  },
  [FORECAST_EMPTY_STATES_TYPES.NO_CHANCE]: {
    Illustration: QuestionMarksDudeIllustration,
    title: 'Forecasting Unavailable',
    Description: (
      <>
        Unfortunately, our calculations indicate that you will not be able to
        finish the project within the next 15 weeks. <br />
        Consider reducing the project scope or adding resources to expedite the
        work.
      </>
    )
  },
  [FORECAST_EMPTY_STATES_TYPES.DEFAULT]: {
    Illustration: () => <OopsIllustration width='20rem' />,
    title: 'Cannot present Forecast',
    Description: (
      <>
        An error had occurred and we couldn't provide you with the requested
        forecast. <br />
        Please try again in a few minutes or contact support for assistance.
      </>
    )
  }
};

const useEmptyStateConfig = ({ type }) => {
  const {
    Illustration = null,
    title = '',
    Description = null,
    Actions = null
  } = useMemo(
    () =>
      emptyStateViews[type] ||
      emptyStateViews[FORECAST_EMPTY_STATES_TYPES.DEFAULT],
    [type]
  );

  return {
    Illustration,
    title,
    Description,
    Actions
  };
};

export default useEmptyStateConfig;
